import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Components
import DiscountBanner from './components/DiscountBanner';
import CookieConsentBanner from './components/CookieConsentBanner';

// Pages
import LandingPage from './pages/LandingPage';
import SignupPage from './pages/SignupPage';
import SigninPage from './pages/SigninPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CreateAvatarPage from './pages/CreateAvatarPage';
import AvatarChatPage from './pages/AvatarChatPage';
import ProjectChatPage from './pages/ProjectChatPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AddonPaymentSuccessPage from './pages/AddonPaymentSuccessPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import BetaSignupPage from './pages/BetaSignupPage';
import BetaExpiredPage from './pages/BetaExpiredPage';
import RelationCaptureScreen from './pages/RelationCaptureScreen';
import DemoPage from './pages/DemoPage';
import { LoadingSpinner } from './components';
import {
  isSubscriptionActive,
  fetchActiveSubscriptionFromApi,
  getApiBase,
  isGiftInvitee,
  canAccessInviteeChat,
} from './utils/freeTrial';

function readCachedUserData() {
  try {
    const raw = sessionStorage.getItem('user_data');
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

async function refreshUserFromApi(token) {
  try {
    const res = await fetch(`${getApiBase()}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return readCachedUserData();
    const body = await res.json().catch(() => ({}));
    const data = body?.data || body;
    if (data && typeof data === 'object') {
      sessionStorage.setItem('user_data', JSON.stringify(data));
      return data;
    }
  } catch (_) { /* fall through */ }
  return readCachedUserData();
}

function RequireSubscription({ children }) {
  // 'pending' = still verifying, 'ok' = allow through, 'blocked' = redirect
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        if (!cancelled) setStatus('blocked');
        return;
      }

      const userData = await refreshUserFromApi(token);

      // Gift invitees and paid send-invite users use the inviter's avatar — no block on chat.
      if (
        isGiftInvitee(userData) ||
        userData?.subscription?.is_gift_subscription === true ||
        canAccessInviteeChat(userData)
      ) {
        if (!cancelled) setStatus('ok');
        return;
      }

      if (isSubscriptionActive(userData?.subscription)) {
        if (!cancelled) setStatus('ok');
        return;
      }

      const apiSub = await fetchActiveSubscriptionFromApi(token);
      if (isSubscriptionActive(apiSub)) {
        try {
          const merged = { ...(userData || {}), subscription: apiSub };
          sessionStorage.setItem('user_data', JSON.stringify(merged));
        } catch (_) { /* non-critical */ }
        if (!cancelled) setStatus('ok');
        return;
      }

      if (!cancelled) setStatus('blocked');
    };

    verify();
    return () => { cancelled = true; };
  }, []);

  if (status === 'pending') {
    return <LoadingSpinner fullScreen text="Loading your account..." />;
  }
  if (status === 'blocked') return <Navigate to="/select-plan" replace />;
  return children;
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect invite links to signup EXCEPT when the link intentionally targets /signin.
  // - New users should land on /signup
  // - Existing users can land on /signin (email invite logic decides which one to send)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteId = params.get('invite_id');
    const inviteEmail = params.get('invite_email');
    const isSignup = location.pathname === '/signup';
    const isSignin = location.pathname === '/signin';
    const isCreateAvatar = location.pathname === '/create-avatar';
    if ((inviteId || inviteEmail) && !isSignup && !isSignin && !isCreateAvatar) {
      const newParams = new URLSearchParams();
      if (inviteId) newParams.set('invite_id', inviteId);
      if (inviteEmail) newParams.set('invite_email', inviteEmail);
      const avatarId = params.get('avatar_id');
      const iWillPay = params.get('i_will_pay');
      if (avatarId) newParams.set('avatar_id', avatarId);
      if (iWillPay) newParams.set('i_will_pay', iWillPay);
      navigate(`/signup?${newParams.toString()}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return (
    <div className="app">
      <DiscountBanner />
      <CookieConsentBanner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signin" element={<SigninPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/create-avatar" element={<CreateAvatarPage />} />
        <Route path="/chat" element={<RequireSubscription><AvatarChatPage /></RequireSubscription>} />
        <Route path="/select-plan" element={<PlanSelectionPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/addon/success" element={<AddonPaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PlanSelectionPage />} />
        <Route path="/projects" element={<ProjectChatPage />} />
        <Route path="/legal" element={<PrivacyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/beta" element={<BetaSignupPage />} />
        <Route path="/beta-expired" element={<BetaExpiredPage />} />
        <Route path="/relation-capture" element={<RelationCaptureScreen />} />
        {/* Catch-all route - redirect to home if route not found */}
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </div>
  );
}

export default App;
